import { FC, useState } from 'react';
import { Chip, Autocomplete, TextField, InputAdornment, Button, Typography } from '@mui/material';
import clsx from 'clsx';
import { TaskLabel } from '@demind-inc/core';

import './TaskLabelsSelect.scss';
import { TaskLabelSelectOption } from './TaskLabelSelectOption';
import {
  useAddTaskLabel,
  useAuthContext,
  useDeleteTaskLabel,
  useUpdateTaskLabel,
} from '../../../data-access';
import { CloseOutlined } from '@mui/icons-material';
import { mdiUnfoldMoreHorizontal } from '@mdi/js';
import Icon from '@mdi/react';
import CreateLabelDialog from './CreateLabelDialog';
import { useTranslation } from 'react-i18next';

interface TaskLabelsSelectProps {
  className?: string;
  allLabels: TaskLabel[];
  selectedLabels: TaskLabel[];
  setSelectedLabels: (newLabels: TaskLabel[]) => void;
  disabled?: boolean;
}

const TaskLabelsSelect: FC<TaskLabelsSelectProps> = ({
  className,
  allLabels = [],
  selectedLabels = [],
  setSelectedLabels,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const safeAllLabels = Array.isArray(allLabels) ? allLabels : [];
  const safeSelectedLabels = Array.isArray(selectedLabels) ? selectedLabels : [];
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createDialogInitialName, setCreateDialogInitialName] = useState('');
  const [createDialogInitialColor, setCreateDialogInitialColor] = useState<string>();

  const { user } = useAuthContext();
  const { updateTaskLabel } = useUpdateTaskLabel();
  const { deleteTaskLabel, isPending: isDeletingLabel } = useDeleteTaskLabel();
  const { addTaskLabel, isPending: isCreatingLabel } = useAddTaskLabel();

  const handleUpdateLabel = (label: TaskLabel) => {
    updateTaskLabel({
      taskLabelGroupId: user.taskLabelsGroupId!,
      labelId: label.id,
      newLabel: label,
    });
  };

  const trimmedInput = inputValue.trim();
  const showCreateButton =
    trimmedInput && !safeAllLabels.some((l) => l.name.toLowerCase() === trimmedInput.toLowerCase());
  const options = showCreateButton
    ? [...safeAllLabels, { id: '__CREATE__', name: trimmedInput } as TaskLabel]
    : [...safeAllLabels, { id: '__CREATE_EMPTY__', name: '' } as TaskLabel];

  const openCreateDialog = (name: string) => {
    setCreateDialogInitialName(name);
    setCreateDialogInitialColor(undefined);
    setCreateDialogOpen(true);
  };

  const handleSaveLabel = async (label: Omit<TaskLabel, 'id'>) => {
    if (!user.taskLabelsGroupId) return;

    try {
      const createdLabel = await addTaskLabel({
        taskLabelGroupId: user.taskLabelsGroupId,
        newLabel: label,
      });
      setInputValue('');

      const nextSelected = safeSelectedLabels.some((l) => l.id === createdLabel.id)
        ? safeSelectedLabels
        : [...safeSelectedLabels, createdLabel];
      setSelectedLabels(nextSelected);

      setCreateDialogOpen(false);
      setIsOpen(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteLabel = async (label: TaskLabel) => {
    if (!user.taskLabelsGroupId) return;

    await deleteTaskLabel({
      taskLabelGroupId: user.taskLabelsGroupId,
      labelId: label.id,
    });

    setSelectedLabels(safeSelectedLabels.filter((item: TaskLabel) => item.id !== label.id));
  };

  return (
    <>
      <Autocomplete
        multiple
        className={clsx('task-labels-select', className)}
        id="tags-filled"
        key="labels-select"
        options={options}
        value={safeSelectedLabels}
        disabled={disabled}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        getOptionLabel={(option: TaskLabel) => option.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        disableCloseOnSelect
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={(event, reason) => {
          const nativeEvent = event.nativeEvent as unknown as FocusEvent;
          const relatedTarget = nativeEvent.relatedTarget as HTMLElement;

          // Keep open if focus moves to color picker content or its parent popover
          if (
            relatedTarget?.querySelector('.color-picker-dropdown__content') ||
            relatedTarget?.closest('.color-picker-dropdown__content')
          ) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }

          setIsOpen(false);
        }}
        slotProps={{
          paper: {
            className: 'task-labels-select__menu',
          },
          listbox: {
            className: 'task-labels-select__listbox',
          },
          popper: {
            className: 'task-labels-select__popper',
          },
        }}
        componentsProps={{
          popper: {
            className: 'task-labels-select__popper',
          },
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation(); // Avoid a modal from closing by key down
          }
        }}
        onChange={(event, val) => {
          const filtered = val.filter((label) => label.id !== '__CREATE__');
          setSelectedLabels(filtered);
          if (val.some((label) => label.id === '__CREATE__')) {
            openCreateDialog(trimmedInput);
          }
        }}
        renderOption={(props, option) => {
          if (!option) return null;

          if (option.id === '__CREATE__' || option.id === '__CREATE_EMPTY__') {
            return (
              <li
                {...props}
                className={clsx('task-labels-select__listbox__option')}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openCreateDialog(option.name);
                }}
              >
                <Button className="task-labels-select__create-button" variant="text">
                  <Typography variant="body2">
                    {option.id === '__CREATE__'
                      ? t('task.labels.createNewLabelWithName', { name: option.name })
                      : t('task.labels.createNewLabel')}
                  </Typography>
                </Button>
              </li>
            );
          }
          return (
            <li
              {...props}
              className={clsx(
                'task-labels-select__listbox__option',
                safeSelectedLabels.some((label) => label.id === option.id) &&
                  'task-labels-select__listbox__option--selected'
              )}
            >
              <TaskLabelSelectOption
                label={option}
                allLabels={safeAllLabels}
                onUpdateLabel={handleUpdateLabel}
                onDeleteLabel={handleDeleteLabel}
                isDeletingLabel={isDeletingLabel}
              />
            </li>
          );
        }}
        renderTags={(values, getTagProps) =>
          (Array.isArray(values)
            ? values.filter((label): label is TaskLabel => Boolean(label))
            : []
          ).map((option: TaskLabel, index: number) => {
            const { key, ...tagProps } = getTagProps({ index });

            return (
              <Chip
                {...tagProps}
                icon={
                  <span
                    className="task-labels-select__tag__dot"
                    style={{ backgroundColor: option?.color || '#680ddb' }}
                  />
                }
                deleteIcon={<CloseOutlined className="task-labels-select__tag__delete-icon" />}
                className={clsx('task-labels-select__tag')}
                label={
                  <div className="task-labels-select__tag__label">
                    <div className="task-labels-select__tag__label__name">
                      {option?.name || t('task.labels.noName')}
                    </div>
                  </div>
                }
                key={key}
              />
            );
          })
        }
        renderInput={(params) => {
          const { InputProps, ...other } = params;
          return (
            <TextField
              {...other}
              variant="filled"
              placeholder={t('task.labels.label')}
              InputLabelProps={{ style: { display: 'none' } }}
              InputProps={{
                ...InputProps,
                endAdornment: (
                  <>
                    {InputProps?.endAdornment}
                    <InputAdornment position="end" className="task-labels-select__icon-adornment">
                      <Icon
                        path={mdiUnfoldMoreHorizontal}
                        size="15px"
                        className="task-labels-select__icon"
                      />
                    </InputAdornment>
                  </>
                ),
              }}
            />
          );
        }}
      />
      <CreateLabelDialog
        visible={createDialogOpen}
        initialLabelName={createDialogInitialName}
        initialLabelColor={createDialogInitialColor}
        existingLabels={safeAllLabels}
        onClose={() => {
          setCreateDialogOpen(false);
        }}
        onSave={handleSaveLabel}
        isLoading={isCreatingLabel}
      />
    </>
  );
};

export default TaskLabelsSelect;
